<!--
 * @Description: Laboratory shell layout (adds top tabs for tools/templates) while keeping default layout structure.
-->
<template>
    <div>
        <!-- Keep the same header behavior as default layout -->
        <!-- 使用 ClientOnly 避免 SSR 水合不匹配（isSmallScreen 在服务端为 false，客户端可能为 true） -->
        <ClientOnly>
            <AppHeader v-if="showAppHeaderOnHistoryMobile" />
            <MobileHomeHeader v-else />
        </ClientOnly>

        <main class="flex-1 bg-default min-h-screen">
            <app-sidebar></app-sidebar>
            <div class="mx-auto w-full max-md:pl-0 pt-5 max-lg:ml-0 lg:flex-1 max-md:!px-4 md:pr-5 transition-[padding-left] duration-300 ease-in-out max-md:pb-24"
                :style="{ '--sidebar-width': `${sidebarWidth}px` }" :class="['pl-[var(--sidebar-width)]']">
                <!-- Laboratory tabs -->
                <ClientOnly>
                    <div v-if="isSmallScreen" class="lab-tabs">
                        <span :class="{ active: activeTab === 'tools_image' }" @click="goToolsImage">
                            {{ t('imageTools') }}
                        </span>
                        <span :class="{ active: activeTab === 'tools_video' }" @click="goToolsVideo">
                            {{ t('videoTools') }}
                        </span>
                        <span :class="{ active: activeTab === 'video_template' }" @click="goVideoEffects">
                            {{ t('menus.videoEffects') }}
                        </span>
                        <span :class="{ active: activeTab === 'image_template' }" @click="goPhotoEffects">
                            {{ t('menus.photoEffects') }}
                        </span>
                    </div>
                </ClientOnly>

                <slot />
            </div>
        </main>

        <!-- 移动端底部菜单：使用 ClientOnly 避免 SSR 水合不匹配 -->
        <ClientOnly>
            <mobile-footer-menu v-if="isSmallScreen" class="md:hidden block"></mobile-footer-menu>
        </ClientOnly>

        <!-- 签到组件 - 移动端需要单独渲染（AppSidebar 在移动端 CSS 隐藏，需要独立的事件监听器） -->
        <ClientOnly>
            <SignInButton v-if="SIGN_IN_ENABLED && userInfo && isSmallScreen" />
        </ClientOnly>
    </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useAppStore } from '@/stores/app'
import { useUserStore } from '@/stores/user'
import { useI18n } from '@/composables/useI18n'
import { SIGN_IN_ENABLED } from '~/utils/feature-flags'

const { t } = useI18n()
const appStore = useAppStore()
const userStore = useUserStore()

// 用户信息
const userInfo = computed(() => userStore.userInfo)
const isSmallScreen = computed(() => appStore?.isSmallScreen ?? false)
const sidebarWidth = computed(() => (appStore?.sidebarWidth ?? 0) + 15)
const route = useRoute()
const localePath = useLocalePath()

const getCurrentPathname = () => {
    // Ensure this depends on reactive route for SPA navigation.
    const _routePath = route.path
    return import.meta.client && typeof window !== 'undefined'
        ? window.location.pathname
        : _routePath || '/'
}

const showAppHeaderOnHistoryMobile = computed(() => {
    const p = getCurrentPathname()
    const isHistory = /^\/(?:[a-z]{2}\/)?history(?:\/|$)/.test(p)
    return isSmallScreen.value && isHistory
})

const activeTab = computed(() => {
    const p = route.path || ''
    if (p.includes('/ai-tools')) {
        return route.query.tab === 'video' ? 'tools_video' : 'tools_image'
    }
    if (p.includes('/video-effects')) return 'video_template'
    if (p.includes('/photo-effects')) return 'image_template'
    return 'tools_image'
})

const goToolsImage = async () => {
    // Keep canonical URL clean: image tools is the default view -> no query.
    await navigateTo(localePath('/ai-tools'))
}
const goToolsVideo = async () => {
    await navigateTo(localePath({ path: '/ai-tools', query: { tab: 'video' } }))
}
const goVideoEffects = async () => {
    await navigateTo(localePath('/video-effects'))
}
const goPhotoEffects = async () => {
    await navigateTo(localePath('/photo-effects'))
}
</script>

<style scoped lang="less">
.lab-tabs {
    display: flex;
    gap: 7px;
    margin-bottom: 10px;
    flex-shrink: 0;

    @media (max-width: 768px) {
        position: sticky;
        top: 52px;
        z-index: 40;
        background: rgba(13, 14, 18, 0.95);
        backdrop-filter: blur(8px);
        margin: -20px -12px 10px -12px;
        padding: 12px 12px 10px 12px;
        overflow-x: auto;
        white-space: nowrap;
        border-bottom: 1px solid rgba(255, 255, 255, 0.05);

        &::-webkit-scrollbar {
            display: none;
        }

        scrollbar-width: none;
    }

    span {
        cursor: pointer;
        width: fit-content;
        padding: 7px 16px;
        font-size: 12px;
        font-weight: 600;
        border-radius: 9999px;
        border: 1px solid rgba(255, 255, 255, 0.12);
        background: rgba(39, 39, 42, 0);
        color: #9ca3af;
        flex-shrink: 0;

        &.active {
            background: #fff;
            color: #000;
        }
    }
}
</style>
